// Controlled edit form for a coworker's own profile fields — reused by both
// the admin coworker edit page (Task 4, passes `coworkerId`) and the
// self-service edit page (Task 5, which will likely pass a `fields` subset
// and/or its own `photoHandlers` hitting `/api/coworkers/me` instead of
// `/api/coworkers/:id`). Validates mobilePhone (E.164) and birthDate
// (YYYY-MM-DD) client-side, mirroring
// apps/api/src/validators/coworkerSchema.ts#updateCoworkerProfileSchema —
// the server re-validates regardless, this is just fail-fast UX.
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Empty, Input, Loader, Text } from '@cloudflare/kumo'
import { TrashIcon } from '@phosphor-icons/react'
import { formatPhoneAsYouType, isValidMobilePhone, normalizeTaxId, toE164 } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import {
  deleteCoworkerFacePhoto,
  getCoworkerFacePhoto,
  uploadCoworkerFacePhoto,
} from '../../api/coworkers'
import {
  ALL_INDIVIDUAL_TAX_ID_TYPES,
  applyDigitMask,
  PASSPORT_TAX_ID_TYPE,
  SINGLE_INDIVIDUAL_TAX_ID_TYPE,
  TOGGLEABLE_TAX_ID_TYPE_ID,
} from '../../lib/taxIdFields'

// `cpf` is NOT a renderable field key (below) — it stays a data field on
// `CoworkerEditFormValue`/`CoworkerEditFormPatch` only, for API round-trip
// compatibility with the legacy `cpf` column (still accepted, optional, by
// `updateCoworkerProfileSchema` — apps/api/src/validators/coworkerSchema.ts).
// The UI no longer exposes it: it was fully redundant with
// taxIdType/taxIdValue (see the CPF/passport field below), which is what
// every write path (enrollment, this form) now populates.
export type CoworkerEditFieldKey =
  | 'name'
  | 'email'
  | 'mobilePhone'
  | 'taxIdCountry'
  | 'taxIdType'
  | 'taxIdValue'
  | 'birthDate'

export const ALL_COWORKER_EDIT_FIELDS: CoworkerEditFieldKey[] = [
  'name',
  'email',
  'mobilePhone',
  'taxIdCountry',
  'taxIdType',
  'taxIdValue',
  'birthDate',
]

export interface CoworkerEditFormValue {
  name: string
  email: string
  mobilePhone: string | null
  cpf: string | null
  taxIdCountry: string | null
  taxIdType: string | null
  taxIdValue: string | null
  birthDate: string | null
}

/** Currently identical to `CoworkerEditFormValue` — the form always submits
 * every editable field it renders (PATCH-idempotent on the server side),
 * not a sparse diff. Kept as a distinct alias so callers/tests express
 * intent (the value passed to `onSave`) rather than the initial value. */
export type CoworkerEditFormPatch = CoworkerEditFormValue

export interface CoworkerPhotoHandlers {
  getPhoto: () => Promise<Blob | null>
  uploadPhoto: (photoBase64: string, consent: boolean) => Promise<void>
  deletePhoto: () => Promise<void>
}

export interface CoworkerEditFormProps {
  value: CoworkerEditFormValue
  onSave: (patch: CoworkerEditFormPatch) => Promise<void>
  /** Which fields to render, in order. Defaults to all — self-service reuse
   * can pass a subset to omit fields it doesn't want to expose. */
  fields?: CoworkerEditFieldKey[]
  /** When set (and `photoHandlers` isn't), the face-photo section wires
   * itself to `/api/coworkers/:id/face-photo` for this id via `useAuth0`. */
  coworkerId?: string
  /** Explicit face-photo handlers — takes priority over `coworkerId`. Lets a
   * caller (e.g. self-service) point at a different endpoint, or omit the
   * face-photo section by passing neither this nor `coworkerId`. */
  photoHandlers?: CoworkerPhotoHandlers
}

type FormErrors = Partial<Record<'mobilePhone' | 'birthDate', string>>

const BIRTH_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

interface FormState {
  name: string
  email: string
  mobilePhone: string
  cpf: string
  taxIdCountry: string
  taxIdType: string
  taxIdValue: string
  birthDate: string
}

/** Seeds the mobilePhone field for display: a stored E.164 value (e.g.
 * `+5511972004215`, saved before this formatting existed, or written by a
 * path that doesn't format on the way in) is reformatted through the same
 * as-you-type formatter used while editing (`formatPhoneAsYouType`), so a
 * previously-saved coworker sees `+55 11 97200-4215` instead of the raw
 * digit string. `formatPhoneAsYouType` never throws; the `|| raw` fallback
 * covers the case where it can't format the value (e.g. garbage input) and
 * would otherwise return it unchanged or empty — display raw rather than
 * silently blanking the field.
 */
function formatStoredPhoneForDisplay(mobilePhone: string | null): string {
  const raw = mobilePhone ?? ''
  if (!raw) return ''
  return formatPhoneAsYouType(raw, 'BR') || raw
}

/** Seeds the taxIdValue field for display: applies the *effective* tax-id
 * type's digit mask (e.g. CPF's `000.000.000-00`) to a stored raw value —
 * the effective type is `value.taxIdType`, falling back to the same default
 * (`SINGLE_INDIVIDUAL_TAX_ID_TYPE`) `toFormState` uses for the type field
 * itself, so the mask always matches what's actually rendered.
 *
 * Truncation guard: `applyDigitMask` (see taxIdFields.ts) walks the mask
 * and STOPS once it runs out of placeholders, silently dropping any extra
 * digits. Applying it to a value with too many (or too few) digits than the
 * mask expects would therefore either corrupt the display (drop real
 * digits) or misleadingly present a partial value as complete. So the mask
 * is only applied when the stored value's digit count exactly matches the
 * mask's placeholder count; anything else — including passport, which has
 * no `mask` at all — is shown raw. Never corrupt data to make it pretty.
 */
function formatStoredTaxIdForDisplay(taxIdType: string | null, taxIdValue: string | null): string {
  const raw = taxIdValue ?? ''
  if (!raw) return ''
  const effectiveTypeId = taxIdType ?? SINGLE_INDIVIDUAL_TAX_ID_TYPE?.id
  const effectiveType = ALL_INDIVIDUAL_TAX_ID_TYPES.find((type) => type.id === effectiveTypeId)
  const mask = effectiveType?.mask
  if (!mask) return raw
  const digitCount = raw.replace(/\D/g, '').length
  const placeholderCount = (mask.match(/0/g) ?? []).length
  if (digitCount !== placeholderCount) return raw
  return applyDigitMask(raw, mask)
}

function toFormState(value: CoworkerEditFormValue): FormState {
  return {
    name: value.name,
    email: value.email,
    mobilePhone: formatStoredPhoneForDisplay(value.mobilePhone),
    // Legacy field — no longer editable here, round-tripped unchanged (see
    // the `CoworkerEditFieldKey` comment above).
    cpf: value.cpf ?? '',
    // Defaults to the single selectable individual tax-id type (BR/CPF)
    // when unset, same as EnrollPage/MyEnrollPage's `emptyForm` — so a
    // coworker who has never had a CPF/passport recorded still lands on the
    // CPF input rather than an empty/undefined mode.
    taxIdCountry: value.taxIdCountry ?? SINGLE_INDIVIDUAL_TAX_ID_TYPE?.country ?? '',
    taxIdType: value.taxIdType ?? SINGLE_INDIVIDUAL_TAX_ID_TYPE?.id ?? '',
    taxIdValue: formatStoredTaxIdForDisplay(value.taxIdType, value.taxIdValue),
    birthDate: value.birthDate ?? '',
  }
}

/** Resolves the face-photo handlers to use: an explicit `photoHandlers`
 * argument always wins; otherwise, when `coworkerId` is set, builds default
 * handlers against the admin `/api/coworkers/:id/face-photo` client.
 * Returns `undefined` when neither is provided (no face-photo section to
 * render). Extracted out of `CoworkerEditForm` and exported so a caller that
 * wants to render `CoworkerFacePhotoSection` standalone (without the rest of
 * the form, e.g. a "Face photo" tab) can obtain handlers the same way the
 * form does internally. */
export function useCoworkerPhotoHandlers(
  coworkerId?: string,
  photoHandlers?: CoworkerPhotoHandlers,
): CoworkerPhotoHandlers | undefined {
  const { getAccessTokenSilently } = useAuth0()

  return useMemo<CoworkerPhotoHandlers | undefined>(() => {
    if (photoHandlers) return photoHandlers
    if (!coworkerId) return undefined
    return {
      getPhoto: async () => {
        const token = await getAccessTokenSilently()
        return getCoworkerFacePhoto(coworkerId, token)
      },
      uploadPhoto: async (photoBase64: string, consent: boolean) => {
        const token = await getAccessTokenSilently()
        await uploadCoworkerFacePhoto(coworkerId, photoBase64, consent, token)
      },
      deletePhoto: async () => {
        const token = await getAccessTokenSilently()
        await deleteCoworkerFacePhoto(coworkerId, token)
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `getAccessTokenSilently`
    // is stable in the real Auth0 provider; omitted so a test double
    // returning a fresh function per render can't spin this into a refetch loop.
  }, [photoHandlers, coworkerId])
}

export function CoworkerEditForm({
  value,
  onSave,
  fields = ALL_COWORKER_EDIT_FIELDS,
  coworkerId,
  photoHandlers,
}: CoworkerEditFormProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<FormState>(() => toFormState(value))
  const [errors, setErrors] = useState<FormErrors>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm(toFormState(value))
  }, [value])

  function setField<K extends keyof FormState>(key: K, next: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: next }))
  }

  const showField = (key: CoworkerEditFieldKey) => fields.includes(key)

  /** Live-formats the mobile phone field as the user types — mirrors
   * EnrollPage.tsx#handlePhoneChange (see its comment for the
   * skip-formatting-while-deleting rationale and the empty-format
   * fallback). Also clears a previously-shown mobilePhone error as soon as
   * the value becomes valid, without requiring another submit. mobilePhone
   * is optional here (unlike enrollment) — an empty value is always valid. */
  function handlePhoneChange(raw: string) {
    const isDeleting = raw.length < form.mobilePhone.length
    let next = raw
    if (!isDeleting) {
      next = formatPhoneAsYouType(raw, 'BR') || raw
    }
    setField('mobilePhone', next)
    if (errors.mobilePhone) {
      const trimmed = next.trim()
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, mobilePhone: undefined }))
        return
      }
      const e164 = toE164(trimmed, 'BR')
      if (e164 && isValidMobilePhone(e164)) {
        setErrors((prev) => ({ ...prev, mobilePhone: undefined }))
      }
    }
  }

  /** Live-formats the CPF field with its `mask` metadata as the user types
   * — mirrors EnrollPage.tsx#handleCpfChange. Submission still normalizes
   * to digits-only via `normalizeTaxId`, unchanged. */
  function handleCpfChange(raw: string) {
    const mask = SINGLE_INDIVIDUAL_TAX_ID_TYPE?.mask
    const isDeleting = raw.length < form.taxIdValue.length
    const next = !isDeleting && mask ? applyDigitMask(raw, mask) : raw
    setField('taxIdValue', next)
  }

  /** Switches the tax-id field to passport mode, clearing the (possibly
   * half-typed) CPF value so it can't be submitted as a passport number —
   * mirrors EnrollPage.tsx#switchToPassportMode. `taxIdCountry` is left
   * as-is. */
  function switchToPassportMode() {
    setForm((prev) => ({ ...prev, taxIdType: TOGGLEABLE_TAX_ID_TYPE_ID, taxIdValue: '' }))
  }

  /** Inverse of `switchToPassportMode` — back to CPF, clearing the passport
   * value so it can't be submitted as a CPF. */
  function switchToCpfMode() {
    setForm((prev) => ({
      ...prev,
      taxIdType: SINGLE_INDIVIDUAL_TAX_ID_TYPE?.id ?? '',
      taxIdValue: '',
    }))
  }

  function validate(): FormErrors {
    const next: FormErrors = {}
    const trimmedPhone = form.mobilePhone.trim()
    if (trimmedPhone) {
      const e164 = toE164(trimmedPhone, 'BR')
      if (!e164 || !isValidMobilePhone(e164)) {
        next.mobilePhone = t('enroll.validation.invalidMobile')
      }
    }
    if (form.birthDate.trim() && !BIRTH_DATE_REGEX.test(form.birthDate.trim())) {
      next.birthDate = t('coworkerEdit.validation.birthDate')
    }
    return next
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    const trimmedPhone = form.mobilePhone.trim()
    const trimmedTaxIdValue = form.taxIdValue.trim()
    const patch: CoworkerEditFormPatch = {
      name: form.name.trim(),
      email: form.email.trim(),
      // `validate()` above already confirmed this parses to a valid mobile
      // (or that it's empty) — the non-null assertion reflects that
      // guarantee for the non-empty case.
      mobilePhone: trimmedPhone ? toE164(trimmedPhone, 'BR')! : null,
      cpf: form.cpf.trim() || null,
      taxIdCountry: form.taxIdCountry.trim() || null,
      taxIdType: form.taxIdType.trim() || null,
      taxIdValue: trimmedTaxIdValue ? normalizeTaxId(form.taxIdType, trimmedTaxIdValue) : null,
      birthDate: form.birthDate.trim() || null,
    }

    setSaving(true)
    try {
      await onSave(patch)
    } finally {
      setSaving(false)
    }
  }

  // Memoized (inside the hook) so the photo section's mount effect doesn't
  // refetch on every keystroke elsewhere in the form.
  const resolvedPhotoHandlers = useCoworkerPhotoHandlers(coworkerId, photoHandlers)

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
      {showField('name') && (
        <Input
          label={t('coworkerEdit.fields.name')}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          required
        />
      )}

      {showField('email') && (
        <Input
          label={t('coworkerEdit.fields.email')}
          type="email"
          value={form.email}
          onChange={(e) => setField('email', e.target.value)}
          description={t('coworkerEdit.fields.emailAuth0Warning')}
          required
        />
      )}

      {showField('mobilePhone') && (
        <Input
          label={t('coworkerEdit.fields.mobilePhone')}
          type="tel"
          value={form.mobilePhone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          error={errors.mobilePhone}
          description={t('coworkerEdit.fields.mobilePhoneHint')}
          inputMode="tel"
        />
      )}

      {/* The legacy `cpf` field and the generic taxIdCountry/taxIdType
          free-text inputs are gone from the UI (redundant with, and less
          safe than, the masked CPF/passport field below) — see the
          `CoworkerEditFieldKey` comment above. `cpf` still round-trips on
          submit unchanged; `taxIdCountry`/`taxIdType` are driven by the
          toggle below instead of free text. */}
      {showField('taxIdValue') &&
        (SINGLE_INDIVIDUAL_TAX_ID_TYPE ? (
          form.taxIdType === TOGGLEABLE_TAX_ID_TYPE_ID && PASSPORT_TAX_ID_TYPE ? (
            <>
              <Input
                label={PASSPORT_TAX_ID_TYPE.label}
                description={t('enroll.fields.taxIdValueHint', {
                  example: PASSPORT_TAX_ID_TYPE.example,
                })}
                value={form.taxIdValue}
                onChange={(e) => setField('taxIdValue', e.target.value)}
              />
              <Button variant="ghost" size="sm" type="button" onClick={switchToCpfMode}>
                {t('enroll.fields.taxIdSwitchToCpf')}
              </Button>
            </>
          ) : (
            <>
              <Input
                label={SINGLE_INDIVIDUAL_TAX_ID_TYPE.label}
                description={t('enroll.fields.taxIdValueHint', {
                  example: SINGLE_INDIVIDUAL_TAX_ID_TYPE.example,
                })}
                value={form.taxIdValue}
                onChange={(e) => handleCpfChange(e.target.value)}
                inputMode="numeric"
              />
              <Button variant="ghost" size="sm" type="button" onClick={switchToPassportMode}>
                {t('enroll.fields.taxIdSwitchToPassport')}
              </Button>
            </>
          )
        ) : null)}

      {showField('birthDate') && (
        <Input
          label={t('coworkerEdit.fields.birthDate')}
          placeholder="YYYY-MM-DD"
          value={form.birthDate}
          onChange={(e) => setField('birthDate', e.target.value)}
          error={errors.birthDate}
          description={t('coworkerEdit.fields.birthDateHint')}
        />
      )}

      {resolvedPhotoHandlers && <CoworkerFacePhotoSection handlers={resolvedPhotoHandlers} />}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="primary" type="submit" loading={saving}>
          {t('coworkerEdit.save')}
        </Button>
      </div>
    </form>
  )
}

export interface CoworkerFacePhotoSectionProps {
  handlers: CoworkerPhotoHandlers
}

/** Preview/upload/delete card for the coworker's face photo, scoped to
 * whatever `handlers` the parent resolved (see `resolvedPhotoHandlers`
 * above). Follows the loading/preview/object-URL-cleanup idiom of
 * `apps/admin/src/components/face-photo/face-photo-card.tsx`, plus an
 * upload control (file input -> base64 -> consent checkbox -> submit). */
export function CoworkerFacePhotoSection({ handlers }: CoworkerFacePhotoSectionProps) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [pendingBase64, setPendingBase64] = useState<string | null>(null)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null)
  const [consent, setConsent] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const previewObjectUrlRef = useRef<string | null>(null)
  const pendingObjectUrlRef = useRef<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const blob = await handlers.getPhoto()
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current)
      previewObjectUrlRef.current = null
    }
    if (blob) {
      const url = URL.createObjectURL(blob)
      previewObjectUrlRef.current = url
      setPreviewUrl(url)
    } else {
      setPreviewUrl(null)
    }
    setLoading(false)
  }, [handlers])

  useEffect(() => {
    void load()
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current)
        previewObjectUrlRef.current = null
      }
      if (pendingObjectUrlRef.current) {
        URL.revokeObjectURL(pendingObjectUrlRef.current)
        pendingObjectUrlRef.current = null
      }
    }
  }, [load])

  function clearPending() {
    if (pendingObjectUrlRef.current) {
      URL.revokeObjectURL(pendingObjectUrlRef.current)
      pendingObjectUrlRef.current = null
    }
    setPendingBase64(null)
    setPendingPreviewUrl(null)
    setConsent(false)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result === 'string') {
        setPendingBase64(result.split(',')[1] ?? '')
      }
    }
    reader.readAsDataURL(file)

    if (pendingObjectUrlRef.current) {
      URL.revokeObjectURL(pendingObjectUrlRef.current)
    }
    const url = URL.createObjectURL(file)
    pendingObjectUrlRef.current = url
    setPendingPreviewUrl(url)
  }

  async function handleUpload() {
    if (!pendingBase64 || !consent) return
    setUploading(true)
    try {
      await handlers.uploadPhoto(pendingBase64, consent)
      clearPending()
      await load()
    } finally {
      setUploading(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      await handlers.deletePhoto()
      await load()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Text as="h3" variant="heading3">
        {t('coworkerEdit.facePhoto.title')}
      </Text>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader size="lg" />
        </div>
      )}

      {!loading && (
        <>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={t('coworkerEdit.facePhoto.previewAlt')}
              className="h-32 w-32 rounded object-cover"
            />
          ) : (
            <Empty title={t('coworkerEdit.facePhoto.empty')} />
          )}

          {previewUrl && (
            <Button
              variant="secondary-destructive"
              size="sm"
              icon={<TrashIcon size={16} />}
              loading={deleting}
              onClick={() => void handleDelete()}
            >
              {t('coworkerEdit.facePhoto.delete')}
            </Button>
          )}
        </>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-sm">
          {t('coworkerEdit.facePhoto.chooseFile')}
          <input type="file" accept="image/*" onChange={handleFileChange} />
        </label>

        {pendingPreviewUrl && (
          <img
            src={pendingPreviewUrl}
            alt={t('coworkerEdit.facePhoto.pendingPreviewAlt')}
            className="h-32 w-32 rounded object-cover"
          />
        )}

        <Checkbox
          label={t('coworkerEdit.facePhoto.consent')}
          checked={consent}
          onCheckedChange={setConsent}
        />

        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={!pendingBase64 || !consent}
          loading={uploading}
          onClick={() => void handleUpload()}
        >
          {t('coworkerEdit.facePhoto.upload')}
        </Button>
      </div>
    </div>
  )
}
